/**
 * ForgotPasswordDialog (F6 — FUTURE_ENHANCEMENTS).
 *
 * Two-step modal: enter email → check Telegram for a 6-digit code → enter
 * code + new password. The backend deliberately returns the same generic
 * "if an account exists…" message regardless of whether the email is on file,
 * so this UI presents the same text to every user.
 *
 * The user MUST have already configured Telegram from Settings; if they
 * haven't, the backend silently no-ops (still returns the generic message)
 * and the user has no path forward — we surface that constraint in the
 * dialog's helper copy so a stuck user knows why they aren't getting a code.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Button, Alert, Box, Typography, Stepper, Step, StepLabel,
} from '@mui/material';
import { authService } from '../../services/auth.service';

interface Props {
  open: boolean;
  onClose: () => void;
}

const ForgotPasswordDialog: React.FC<Props> = ({ open, onClose }) => {
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setStep(0); setEmail(''); setCode(''); setNewPassword('');
    setConfirmPassword(''); setError(null); setLoading(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const requestCode = async () => {
    setError(null);
    if (!email.trim()) { setError('Email is required'); return; }
    setLoading(true);
    try {
      await authService.forgotPassword(email.trim());
      setStep(1);
    } catch (err: any) {
      // Even rate-limit / network failures fall through to step 1 — the
      // backend won't tell us whether a code was sent, so we present the
      // same UX regardless. Surface only network errors.
      if (err?.response?.status === 429) {
        setError('Too many attempts. Try again in a few minutes.');
      } else if (!err?.response) {
        setError('Network error. Try again.');
      } else {
        setStep(1);
      }
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async () => {
    setError(null);
    if (!/^\d{6}$/.test(code.trim())) { setError('Code must be 6 digits'); return; }
    if (newPassword.length < 12) { setError('Password must be at least 12 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await authService.resetPassword(email.trim(), code.trim(), newPassword);
      setStep(2);
    } catch (err: any) {
      const code = err?.response?.data?.error?.code;
      if (code === 'WEAK_PASSWORD') {
        setError(err?.response?.data?.error?.message || 'Password too weak');
      } else if (code === 'RESET_INVALID') {
        setError('Invalid or expired code. Request a new one and try again.');
      } else {
        setError('Could not reset password. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Reset Password</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          <Step><StepLabel>Email</StepLabel></Step>
          <Step><StepLabel>Code</StepLabel></Step>
          <Step><StepLabel>Done</StepLabel></Step>
        </Stepper>

        {step === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              We'll send a 6-digit reset code to your Telegram (the one you configured under Settings → Telegram).
              If you never set up Telegram, ask the admin to reset your password manually.
            </Typography>
            <TextField
              fullWidth autoFocus label="Email Address" type="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              onKeyDown={(e) => { if (e.key === 'Enter') requestCode(); }}
            />
          </Box>
        )}

        {step === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Check Telegram for a 6-digit code (valid 10 minutes). Enter it below with your new password.
            </Typography>
            <TextField
              fullWidth autoFocus label="6-digit code" margin="normal"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              disabled={loading}
            />
            <TextField
              fullWidth label="New Password" type="password" margin="normal"
              value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              helperText="At least 12 characters, with a mix of upper/lower/digit/symbol"
              disabled={loading}
            />
            <TextField
              fullWidth label="Confirm New Password" type="password" margin="normal"
              value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
            />
          </Box>
        )}

        {step === 2 && (
          <Alert severity="success">
            Password reset successfully. Sign in with your new password.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        {step === 2 ? (
          <Button onClick={handleClose} variant="contained">Close</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>Cancel</Button>
            {step === 0 && (
              <Button onClick={requestCode} variant="contained" disabled={loading}>
                {loading ? 'Sending…' : 'Send Code'}
              </Button>
            )}
            {step === 1 && (
              <Button onClick={submitReset} variant="contained" disabled={loading}>
                {loading ? 'Resetting…' : 'Reset Password'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
